import { Injectable } from '@angular/core';
import { ApiService } from 'app/services/api.service';
import { ApiResponse, Client } from 'app/entities';
import { map } from 'rxjs/operators';
import { Proposal } from 'app/entities/proposal';
import { ProposalLog } from 'app/entities/proposal-log';

/** ORCA-1963: GET proposals/summary */
export interface ProposalListIndicator {
    filter: 'awaiting' | 'expiring' | 'approved_month' | 'receivable';
    count: number;
    amount: number;
}

export interface ProposalListSummary {
    indicators: Record<ProposalListIndicator['filter'], ProposalListIndicator>;
    status_counts: Record<'all' | 'sent' | 'created' | 'approved' | 'billed' | 'paid' | 'refused' | 'expired', { status: number | null; count: number }>;
}

/** ORCA-1964: resposta das ações em massa */
export interface ProposalBulkResult {
    results: { id: number; proposal_number: number | null; success: boolean; message: string | null }[];
    succeeded: number;
    failed: number;
}

@Injectable({
    providedIn: 'root'
})
export class ProposalService extends ApiService {


    storeLocally(proposal: Proposal) {
        localStorage.setItem("copy_proposal", JSON.stringify(proposal))
    }

    getLocalStored() {
        return Proposal.parse(localStorage.getItem("copy_proposal")) || new Proposal()
    }

    findAll(params: any = {}) {
        params = this.paramsFromObject(params);
        return this.httpService.get(`/v1/clients/proposals`, params)
            .pipe(
                map(res => ApiResponse.parseWith(new Proposal, res) as ApiResponse<Proposal[]>)
            )
    }

    /** ORCA-1971: clientes por página (20), com busca por nome, e-mail, CPF/CNPJ e telefone */
    searchClients(search: string, page: number, ids?: number[]) {
        const params: any = { search: search || '', page: String(page) };
        if (ids?.length) {
            params.ids = ids.map(String);
        }
        return this.httpService.get(`/v1/clients/clients/search`, this.paramsFromObject(params))
    }

    preview(data: any) {
        let formData = this.formDataFromObject(data)
        return this.httpService.post(`/v1/clients/preview-proposal`, formData)
    }

    // ORCA-1971: overrides = layout, cores e assinatura ainda não salvos (modelo novo)
    previewExample(templateId?: number, overrides?: any) {
        const data = { ...(templateId ? { template_id: templateId } : {}), ...(overrides || {}) };
        return this.httpService.post(`/v1/clients/preview-proposal/example`, data)
    }

    find(id: any) {
        return this.httpService.get(`/v1/clients/proposals/${id}`)
            .pipe(
                map(res => Proposal.parse(res.data) as Proposal)
            )
    }

    update(id: number, data: any) {
        let formData = this.formDataFromObject(data)
        return this.httpService.post(`/v1/clients/proposals/${id}`, formData)
            .pipe(
                map(res => Proposal.parse(res.data) as Proposal)
            )
    }

    /** ORCA-1931: formas de pagamento padrão da conta para um orçamento novo */
    paymentMethodDefaults() {
        return this.httpService.get(`/v1/clients/proposals/payment-methods/defaults`)
            .pipe(
                map(res => res.data as { methods: any[]; is_account_default: boolean })
            )
    }

    /** ORCA-1937: padrão de "Detalhes dos itens" da conta para um orçamento novo */
    itemDetailsDefaults() {
        return this.httpService.get(`/v1/clients/proposals/item-details/defaults`)
            .pipe(
                map(res => res.data as { item_details: any; is_account_default: boolean })
            )
    }

    /** ORCA-1938: sobe a imagem de um item e devolve a URL */
    uploadItemImage(file: File) {
        const formData = new FormData();
        formData.append('image', file);
        return this.httpService.post(`/v1/clients/proposals/item-image`, formData)
            .pipe(
                map(res => res.data.url as string)
            )
    }

    updateInformation(id: number, data: any) {
        let formData = this.formDataFromObject(data)
        return this.httpService.post(`/v1/clients/proposals/${id}/information`, formData)
            .pipe(
                map(res => Proposal.parse(res.data) as Proposal)
            )
    }

    aprove(id: number, data: any) {
        let formData = this.formDataFromObject(data)

        return this.httpService.post(`/v1/clients/proposals/${id}/approve`, formData)
            .pipe(
                map(res => this.withUndoToken(res))
            )
    }

    faturar(id: number, data: any) {
        let formData = this.formDataFromObject(data)
        return this.httpService.post(`/v1/clients/proposals/${id}/bill`, formData)
            .pipe(
                map(res => this.withUndoToken(res))
            )
    }

    pagar(id: number, data: any) {
        let formData = this.formDataFromObject(data)
        return this.httpService.post(`/v1/clients/proposals/${id}/pay`, formData)
            .pipe(
                map(res => this.withUndoToken(res))
            )
    }

    reprove(id: number, data: any) {
        let formData = this.formDataFromObject(data)
        return this.httpService.post(`/v1/clients/proposals/${id}/reprove`, formData)
            .pipe(
                map(res => this.withUndoToken(res))
            )
    }

    /** ORCA-1964: volta o orçamento ao estado anterior à última ação feita pela lista */
    undo(id: number, undoToken: string) {
        return this.httpService.post(`/v1/clients/proposals/${id}/undo`, { undo_token: undoToken })
            .pipe(
                map(res => Proposal.parse(res.data) as Proposal)
            )
    }

    /** ORCA-1963: indicadores e contagem por status (mesmos filtros da listagem) */
    summary(params: any = {}) {
        params = this.paramsFromObject(params);
        return this.httpService.get(`/v1/clients/proposals/summary`, params)
            .pipe(
                map(res => res.data as ProposalListSummary)
            )
    }

    private withUndoToken(res: any): Proposal {
        const proposal = Proposal.parse(res.data) as Proposal;
        proposal.undoToken = res?.meta?.undo_token ?? null;
        return proposal;
    }

    getProposalToDuplication(proposalToken) {
        return this.httpService.get(`/v1/clients/proposals/${proposalToken}/duplicate`)
            .pipe(
                map(res => Proposal.parse(res.data) as Proposal)
            )
    }

    sendEmail(id: number, contact: any = null) {
        return this.httpService.post(`/v1/clients/proposals/${id}/email`, contact)
            .pipe(
                map(res => Proposal.parse(res.data) as Proposal)
            )
    }

    store(data: any) {
        let formData = this.formDataFromObject(data)
        return this.httpService.post(`/v1/clients/proposals`, formData)
            .pipe(
                map(res => Proposal.parse(res.data) as Proposal)
            )
    }

    destroy(id: number) {
        return this.httpService.delete(`/v1/clients/proposals/${id}`)
    }

    destroyMany(proposalsId: number[]) {
        const idsArray = Array.isArray(proposalsId) ? proposalsId : [proposalsId];
        const params = this.paramsFromObject({ proposalsId: idsArray });
        return this.httpService.delete(`/v1/clients/proposals/destroy-many`, params);
    }

    destroyMassiveWithFilters(filter: any, excludedIds: number[] = []) {
        const idsArray = Array.isArray(excludedIds) ? excludedIds : [excludedIds];
        const params = this.paramsFromObject({
            filter,
            proposalsId: idsArray,
        });
        return this.httpService.delete(`/v1/clients/proposals/destroy-massive`, params);
    }

    /** ORCA-1967: ações em massa da lista, com o resultado de cada orçamento */
    bulkStatus(proposalsId: number[], status: number, date: Date = new Date()) {
        const pad = (n: number) => String(n).padStart(2, '0');
        const local = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
        return this.httpService.post(`/v1/clients/proposals/bulk/status`, { proposalsId, status, date: local })
            .pipe(map(res => res.data as ProposalBulkResult));
    }

    bulkDelete(proposalsId: number[]) {
        return this.httpService.post(`/v1/clients/proposals/bulk/delete`, { proposalsId })
            .pipe(map(res => res.data as ProposalBulkResult));
    }

    bulkRemind(proposalsId: number[]) {
        return this.httpService.post(`/v1/clients/proposals/bulk/remind`, { proposalsId })
            .pipe(map(res => res.data as ProposalBulkResult));
    }

    restore(id: number) {
        return this.httpService.post(`/v1/clients/proposals/${id}/restore`, {});
    }

    restoreMany(proposalsId: number[]) {
        const idsArray = Array.isArray(proposalsId) ? proposalsId : [proposalsId];
        return this.httpService.post(`/v1/clients/proposals/restore-many`, { proposalsId: idsArray });
    }

    restoreMassiveWithFilters(filter: any, excludedIds: number[] = []) {
        const idsArray = Array.isArray(excludedIds) ? excludedIds : [excludedIds];
        return this.httpService.post(`/v1/clients/proposals/restore-massive`, {
            filter,
            proposalsId: idsArray,
        });
    }

    copyLink(id, source: any = null) {
        return this.httpService.post(`/v1/clients/proposals/${id}/copy-link`, {
            source: source
        })
    }

    generatePDF(id) {
        return this.httpService.post(`/v1/clients/proposals/${id}/generate/pdf/logs`, {})
    }

    findLogs(token) {
        return this.httpService.get(`/v1/clients/proposals/${token}/logs`)
            .pipe(
                map(res => ProposalLog.parse(res.data) as ProposalLog)
            )
    }

    getDetailsConfigStructure() {
        return this.httpService.get(`/v1/clients/proposals/details-config-structure`)
            .pipe(
                map(res => res.data)
            )
    }

}
